import React, { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getDevicesByQuery } from '../api/api';
import usePagination from '../components/Pagination/usePagination';
import { EventBusy, InfoOutlined, ReportProblemOutlined, Schedule, SignalCellularAltOutlined } from '@mui/icons-material';
import Pagination from '../components/Pagination/Pagination';
import { useEventTypesStore } from '../features/Events/state/use-event-types-store';
import { useEventsStore } from '../features/Events/state/use-events-store';
import MemoryIcon from '@mui/icons-material/Memory';
import TableTitle from '../components/TableTitle';
import SearchBar from '../features/Events/SearchBar';
import Table from '../components/Table';
import { useObjectsStore } from '../stores';

dayjs.extend(utc);

const LIMIT_EVENTS = 10;
const INITIAL_VALUES = { deviceId: '', categories: [], levels: [], message: '', from: null, to: null, objectId: '' };

const EventsTable = () => {
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT_EVENTS });
  const { data, error, isLoading, total, fetchUserEvents } = useEventsStore();
  const { categories, levels, fetchEventTypes } = useEventTypesStore();
  const [searchValue, setSearchValue] = useState(INITIAL_VALUES);
  const { objects } = useObjectsStore();

  useEffect(() => {
    const get = async () => {
      await fetchEventTypes();
    };
    get();
  }, []);

  useEffect(() => {
    const get = async () => {
      await fetchUserEvents(page, limit, searchValue);
    };

    get();
  }, [page, limit, searchValue]);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value }));
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const loadDeviceOptions = async (inputValue) => {
    const devices = await getDevicesByQuery({ serial_number: inputValue });
    return devices.data.map((device) => ({
      label: device.serial_number,
      value: device.id,
    }));
  };

  const inputs = [
    {
      placeholderText: 'Дата начала',
      type: 'date',
      selected: searchValue.from,
      onChange: (value) => handleSearch(value, 'from'),
      prefix: <Schedule fontSize={'small'} className="text-gray-500 mr-2" />,
    },
    {
      placeholderText: 'Дата окончания',
      type: 'date',
      selected: searchValue.to,
      onChange: (value) => handleSearch(value, 'to'),
      prefix: <EventBusy fontSize={'small'} className="text-gray-500 mr-2" />,
    },
    {
      placeholder: 'Поиск по устройству...',
      type: 'search',
      handleSearch: (value) => handleSearch(value, 'deviceId'),
      prefix: <MemoryIcon fontSize={'small'} className="text-gray-500" />,
      loadOptions: loadDeviceOptions,
    },
    {
      label: 'По категориям',
      options: categories,
      selectedOptions: searchValue.categories,
      type: 'filter',
      setSelectedOptions: (value) => handleSearch(value, 'categories'),
      prefix: <SignalCellularAltOutlined fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      label: 'По уровню',
      options: levels,
      selectedOptions: searchValue.levels,
      type: 'filter',
      setSelectedOptions: (value) => handleSearch(value, 'levels'),
      prefix: <InfoOutlined fontSize={'small'} className={'text-gray-500'} />,
    },
    {
      placeholder: 'Поиск по ошибке',
      type: 'simpleSearch',
      handleSearch: (value) => handleSearch(value, 'message'),
      prefix: <ReportProblemOutlined fontSize={'small'} className="text-gray-500" />,
    },
    {
      options: [
        { name: 'Все', value: '' },
        ...(objects?.map((obj) => ({
          name: `${obj?.address}, кв ${obj?.apartment}`,
          value: obj?.id,
        })) || []),
      ],
      selectedOptions: searchValue.objectId || '',
      type: 'select',
      setSelectedOptions: (value) => handleSearch(value, 'objectId'),
      prefix: <InfoOutlined fontSize={'small'} className={'text-gray-500'} />,
      placeholder: 'По адресу',
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: 'user',
        title: 'Пользователь',
        render: (value) => value.fio,
      },
      {
        key: 'device',
        title: 'Адрес',
        render: (value) => (value?.address ? `${value?.address}, кв. ${value?.apartment}` : '-'),
      },
      {
        key: 'code',
        title: 'Код ошибки',
      },
      {
        key: 'name',
        title: 'Описание',
        render: (value) => <div className={'w-[150px]'}>{value}</div>,
      },
      {
        key: 'category',
        title: 'Категория',
      },
      {
        key: 'level',
        title: 'Уровень',
        render: (value) => <div className={'text-center'}>{levels?.[value]?.icon}</div>,
      },
      {
        key: 'payload',
        title: 'Сообщение',
        render: (value) => <div className={'w-[200px]'}>{value?.message}</div>,
      },
      {
        key: 'occurred',
        title: 'Время',
        render: (value) => (value ? dayjs(value).format('DD.MM.YY HH:mm') : '-'),
      },
    ],
    [levels, objects]
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-[#ededed] to-[#dcdcdc]">
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'События'} />
        <SearchBar inputs={inputs} />
      </div>
      <Table emptyText={'События отсутствуют'} columns={columns} data={data} loading={isLoading} error={error}>
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} />}
      </Table>
    </div>
  );
};
export default EventsTable;
